import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import type { ActiveBuffData } from '@shared/types/messages';

/**
 * Buff/Debuff bar displayed below the player's HP/Mana bars.
 * Shows active buffs/debuffs as small icons with hover tooltips
 * displaying name, effect description, and remaining duration.
 * Uses the item placeholder icon as the buff icon.
 */

const BUFF_ICON_SIZE = 24;
const BUFF_ICON_GAP = 4;
const TOOLTIP_WIDTH = 200;
const TOOLTIP_PADDING = 8;

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

interface BuffIconEntry {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  icon: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
  timerText: Phaser.GameObjects.Text;
  data: ActiveBuffData;
}

export class BuffBar {
  private readonly scene: Phaser.Scene;
  private buffIcons: Map<string, BuffIconEntry> = new Map();
  private tooltip: Phaser.GameObjects.Container | null = null;
  private tooltipBg: Phaser.GameObjects.Rectangle | null = null;
  private tooltipName: Phaser.GameObjects.Text | null = null;
  private tooltipDesc: Phaser.GameObjects.Text | null = null;
  private tooltipTimer: Phaser.GameObjects.Text | null = null;
  private lastSyncTime: number = 0;
  private anchorX: number = 0;
  private anchorY: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createTooltip();
  }

  /**
   * Set the anchor position: buffs start at this X and sit above this Y
   */
  setAnchor(x: number, y: number): void {
    this.anchorX = x;
    this.anchorY = y;
    this.repositionIcons();
  }

  /**
   * Update the buff bar with new data from server.
   * Called when ACTIVE_BUFFS_SYNC message is received.
   */
  public updateBuffs(buffs: ActiveBuffData[]): void {
    this.lastSyncTime = Date.now();

    // Track which buffs are still active
    const activeIds = new Set(buffs.map(b => b.id));

    // Remove expired buff icons
    for (const [id, entry] of this.buffIcons.entries()) {
      if (!activeIds.has(id)) {
        entry.container.destroy();
        this.buffIcons.delete(id);
      }
    }

    // Add or update buff icons
    for (const buff of buffs) {
      const existing = this.buffIcons.get(buff.id);
      if (existing) {
        existing.data = buff;
        this.updateTimerText(existing);
      } else {
        this.createBuffIcon(buff);
      }
    }

    // Reposition all icons
    this.repositionIcons();
  }

  /**
   * Update timer texts each frame (called from scene update)
   */
  public update(): void {
    const elapsed = Date.now() - this.lastSyncTime;
    let needsReposition = false;

    for (const [id, entry] of this.buffIcons.entries()) {
      // Auras don't expire client-side (server controls via sync)
      if (entry.data.isAura) continue;

      const remaining = Math.max(0, entry.data.remainingMs - elapsed);

      // Remove expired buffs
      if (remaining <= 0) {
        entry.container.destroy();
        this.buffIcons.delete(id);
        needsReposition = true;
        continue;
      }

      if (remaining > 60000) {
        entry.timerText.setText(`${Math.ceil(remaining / 60000)}m`);
      } else {
        entry.timerText.setText(`${Math.ceil(remaining / 1000)}s`);
      }

      // Pulse effect when about to expire (< 5s)
      if (remaining < 5000) {
        const alpha = 0.5 + 0.5 * Math.sin(Date.now() / 200);
        entry.bg.setAlpha(alpha);
      } else {
        entry.bg.setAlpha(1);
      }
    }

    if (needsReposition) this.repositionIcons();
  }

  private createBuffIcon(buff: ActiveBuffData): void {
    const container = this.scene.add.container(0, 0);
    container.setDepth(950);
    container.setScrollFactor(0);

    // Background color: aura=blue, buff=green, debuff=red
    const bgColor = buff.isAura ? 0x223388 : buff.isDebuff ? 0x882222 : 0x226622;
    const borderColor = buff.isAura ? 0x4488ff : buff.isDebuff ? 0xff4444 : 0x44ff44;
    const bg = this.scene.add.rectangle(0, 0, BUFF_ICON_SIZE, BUFF_ICON_SIZE, bgColor, 0.85);
    bg.setStrokeStyle(1, borderColor);
    container.add(bg);

    // Icon — usar el ícono del spell asociado si está cargado, sino caer al placeholder.
    let icon: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
    const iconKey =
      buff.icon && this.scene.textures.exists(buff.icon)
        ? buff.icon
        : this.scene.textures.exists('item_icon_noicon')
        ? 'item_icon_noicon'
        : null;
    if (iconKey) {
      icon = this.scene.add.image(0, 0, iconKey);
      icon.setDisplaySize(BUFF_ICON_SIZE - 4, BUFF_ICON_SIZE - 4);
    } else {
      icon = this.scene.add.rectangle(0, 0, BUFF_ICON_SIZE - 6, BUFF_ICON_SIZE - 6,
        buff.isDebuff ? 0xff6666 : 0x66ff66, 0.6);
    }
    container.add(icon);

    // Timer text below the icon (hidden for auras)
    const timerText = this.scene.add.text(0, BUFF_ICON_SIZE / 2 + 8, '', withGameFont({
      fontSize: '8px',
      color: '#ffffff',
      align: 'center',
    }));
    timerText.setOrigin(0.5, 0);
    timerText.setVisible(!buff.isAura);
    container.add(timerText);

    // Interactive for tooltip
    bg.setInteractive({ useHandCursor: false });
    bg.on('pointerover', () => this.showTooltip(buff, container.x, container.y));
    bg.on('pointerout', () => this.hideTooltip());

    const entry: BuffIconEntry = { container, bg, icon, timerText, data: buff };
    this.buffIcons.set(buff.id, entry);
    this.updateTimerText(entry);
  }

  private updateTimerText(entry: BuffIconEntry): void {
    const remaining = entry.data.remainingMs;
    if (remaining > 60000) {
      entry.timerText.setText(`${Math.ceil(remaining / 60000)}m`);
    } else {
      entry.timerText.setText(`${Math.ceil(remaining / 1000)}s`);
    }
  }

  private repositionIcons(): void {
    // Auras first, then buffs, then debuffs
    const entries = Array.from(this.buffIcons.values())
      .sort((a, b) => {
        if (a.data.isAura !== b.data.isAura) return a.data.isAura ? -1 : 1;
        if (a.data.isDebuff !== b.data.isDebuff) return a.data.isDebuff ? 1 : -1;
        return 0;
      });
    // Start at anchorX, grow to the right. Sit above anchorY.
    const startX = this.anchorX + BUFF_ICON_SIZE / 2;
    const y = this.anchorY - BUFF_ICON_SIZE / 2 - 4;

    for (let i = 0; i < entries.length; i++) {
      entries[i].container.setPosition(
        startX + i * (BUFF_ICON_SIZE + BUFF_ICON_GAP),
        y
      );
    }
  }

  private createTooltip(): void {
    this.tooltip = this.scene.add.container(0, 0);
    this.tooltip.setDepth(1200);
    this.tooltip.setScrollFactor(0);
    this.tooltip.setVisible(false);

    this.tooltipBg = this.scene.add.rectangle(0, 0, TOOLTIP_WIDTH, 80, 0x111111, 0.95);
    this.tooltipBg.setStrokeStyle(1, 0x888888);
    this.tooltipBg.setOrigin(0.5, 0);
    this.tooltip.add(this.tooltipBg);

    this.tooltipName = this.scene.add.text(0, TOOLTIP_PADDING, '', withGameFont({
      fontSize: '12px',
      color: '#ffff88',
      fontStyle: 'bold',
      align: 'center',
    }));
    this.tooltipName.setOrigin(0.5, 0);
    this.tooltip.add(this.tooltipName);

    this.tooltipDesc = this.scene.add.text(0, TOOLTIP_PADDING + 18, '', withGameFont({
      fontSize: '10px',
      color: '#cccccc',
      align: 'center',
      wordWrap: { width: TOOLTIP_WIDTH - TOOLTIP_PADDING * 2 },
    }));
    this.tooltipDesc.setOrigin(0.5, 0);
    this.tooltip.add(this.tooltipDesc);

    this.tooltipTimer = this.scene.add.text(0, TOOLTIP_PADDING + 44, '', withGameFont({
      fontSize: '10px',
      color: '#88ff88',
      align: 'center',
    }));
    this.tooltipTimer.setOrigin(0.5, 0);
    this.tooltip.add(this.tooltipTimer);
  }

  private showTooltip(buff: ActiveBuffData, x: number, y: number): void {
    if (!this.tooltip || !this.tooltipName || !this.tooltipDesc || !this.tooltipTimer || !this.tooltipBg) return;

    // Name — debuffs get tag, buffs/auras just the name
    this.tooltipName.setText(buff.isDebuff ? `[Debuff] ${buff.name}` : buff.name);
    this.tooltipName.setColor(buff.isDebuff ? '#ff8888' : '#88ff88');

    // Description — strip "Toggle: " prefix and reserve info
    let desc = buff.description;
    desc = desc.replace(/^Toggle:\s*/i, '');
    desc = desc.replace(/\.\s*Reserva\s+\d+%\s+de\s+maná\.?/i, '.');
    desc = desc.replace(/\.\s*Drena\s+stamina\.?/i, '.');
    this.tooltipDesc.setText(desc.trim());

    // Hide timer from tooltip (already shown on the icon)
    this.tooltipTimer.setText('');

    // Resize bg to fit description
    const descHeight = this.tooltipDesc.height || 14;
    const contentHeight = TOOLTIP_PADDING * 2 + 18 + descHeight + 4;
    this.tooltipBg.setSize(TOOLTIP_WIDTH, contentHeight);

    // Position tooltip below the icon
    this.tooltip.setPosition(x, y + BUFF_ICON_SIZE + 14);
    this.tooltip.setVisible(true);
  }

  private hideTooltip(): void {
    if (this.tooltip) {
      this.tooltip.setVisible(false);
    }
  }

  /**
   * Destroy the buff bar (cleanup)
   */
  public destroy(): void {
    for (const entry of this.buffIcons.values()) {
      entry.container.destroy();
    }
    this.buffIcons.clear();
    if (this.tooltip) {
      this.tooltip.destroy();
      this.tooltip = null;
    }
  }
}
